import { BookingDay } from "./ChatWidget";

/**
 * The booking card's time zone: the customer's browser zone by default, a
 * short list of common zones to switch to, and the card's slots regrouped
 * into days and labelled in the chosen zone (slot starts are UTC instants).
 */

const COMMON_TIME_ZONES = [
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Phoenix",
  "America/Chicago",
  "America/Mexico_City",
  "America/New_York",
  "America/Bogota",
  "America/Halifax",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "UTC",
  "Europe/London",
  "Europe/Lisbon",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Vienna",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Zurich",
  "Europe/Warsaw",
  "Europe/Athens",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Lagos",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Jakarta",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Perth",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export const isValidTimeZone = (zone?: string): zone is string => {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

export const browserTimeZone = (): string | undefined => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimeZone(zone) ? zone : undefined;
  } catch {
    return undefined;
  }
};

/** Minutes east of UTC of a zone at `at` (for sorting the list). */
const offsetMinutes = (zone: string, at: Date): number => {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", {
        timeZone: zone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      } as Intl.DateTimeFormatOptions)
        .formatToParts(at)
        .map((part) => [part.type, part.value]),
    );
    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24,
      Number(parts.minute),
    );
    const minute = Math.floor(at.getTime() / 60000) * 60000;
    return Math.round((asUtc - minute) / 60000);
  } catch {
    return 0;
  }
};

const offsetLabel = (minutes: number) => {
  if (minutes === 0) return "GMT";
  const sign = minutes > 0 ? "+" : "-";
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const rest = abs % 60;
  return `GMT${sign}${hours}${rest ? `:${String(rest).padStart(2, "0")}` : ""}`;
};

/** "Vienna (GMT+2)" */
export const timeZoneOptionLabel = (zone: string, at = new Date()): string => {
  const city =
    zone === "UTC" ? "UTC" : zone.split("/").pop()!.replace(/_/g, " ");
  return zone === "UTC"
    ? city
    : `${city} (${offsetLabel(offsetMinutes(zone, at))})`;
};

/** The selector's zones: the given ones first (browser, card), then the common list, by offset. */
export const timeZoneChoices = (
  extra: (string | undefined)[],
  at = new Date(),
): string[] => {
  const zones = Array.from(
    new Set(
      [...extra, ...COMMON_TIME_ZONES].filter((zone): zone is string =>
        isValidTimeZone(zone),
      ),
    ),
  );
  return zones.sort(
    (a, b) => offsetMinutes(a, at) - offsetMinutes(b, at) || a.localeCompare(b),
  );
};

const safeLocale = (locale?: string) => {
  try {
    return locale ? (Intl as any).getCanonicalLocales(locale)[0] : undefined;
  } catch {
    return undefined;
  }
};

/** The card's slots as days in `zone` ("2026-10-15", "Wed", "15", "11:30"). */
export const bookingDaysIn = (
  days: BookingDay[],
  zone: string,
  locale?: string,
): BookingDay[] => {
  const lang = safeLocale(locale);
  const keyFormat = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const dayFormat = new Intl.DateTimeFormat(lang, {
    timeZone: zone,
    weekday: "short",
    day: "numeric",
  });
  const timeFormat = new Intl.DateTimeFormat(lang, {
    timeZone: zone,
    hour: "numeric",
    minute: "2-digit",
  });
  const starts = Array.from(
    new Set(days.flatMap((day) => (day.slots ?? []).map((slot) => slot.start))),
  )
    .map((start) => ({ start, date: new Date(start) }))
    .filter((entry) => !Number.isNaN(entry.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const byDay = new Map<string, BookingDay>();
  for (const { start, date } of starts) {
    const parts = Object.fromEntries(
      keyFormat.formatToParts(date).map((part) => [part.type, part.value]),
    );
    const key = `${parts.year}-${parts.month}-${parts.day}`;
    if (!byDay.has(key)) {
      const label = Object.fromEntries(
        dayFormat.formatToParts(date).map((part) => [part.type, part.value]),
      );
      byDay.set(key, {
        date: key,
        label: label.weekday ?? key,
        dayNumber: label.day ?? key.slice(8),
        slots: [],
      });
    }
    byDay.get(key)!.slots.push({ start, label: timeFormat.format(date) });
  }
  return Array.from(byDay.values());
};
